export const TEAM_SIGNATURE = 'The Greeley Hat Works Team';
export const RECRAFT_URL = 'https://greeleyhatworks.com/pages/recraft';
/** Remote-fitting and custom options guide. Distinct from the qualities / X-ratings page. */
export const CUSTOM_OPTIONS_URL = 'https://greeleyhatworks.com/pages/custom-hat-options';
/** Custom qualities / X-ratings lineup (Classic, Competitor, Beaver 20, Pure Beaver). */
export const CUSTOM_QUALITIES_URL = 'https://greeleyhatworks.com/pages/custom';
export const CUSTOM_OPTIONS_PHONE = '888-367-2428';

export const CUSTOM_OPTIONS_V1_CHECKLIST = [
  'Brim width (GHW standard is 4¼ inches)',
  'Sweatband color and material',
  'Sweatband wording and foil color',
  'Satin liner color',
  'Hat band trim: ribbon or self band',
  'If self band: buckle choice',
  'If ribbon: style, width or thickness, and keeper choice',
  'Edge binding: yes or no',
  'If edge binding: ribbon color',
  'Shaping: brim choice and crown choice',
] as const;

export const CUSTOM_OPTIONS_V2_CHECKLIST = [
  { text: 'Brim Width: (Our standard is 4¼ inches)', depth: 0 },
  { text: 'Sweatband: Color, material, desired wording, and foil accent color', depth: 0 },
  { text: 'Satin Liner: Color preference', depth: 0 },
  { text: 'Hat Band Trim: Ribbon or self-band', depth: 0 },
  { text: 'If Self Band: Buckle selection', depth: 1 },
  { text: 'If Ribbon: Style, width/thickness, and buckle/keeper choice', depth: 1 },
  { text: 'Shaping: Brim style and crown shape', depth: 0 },
  { text: 'Other Requests: Anything else?', depth: 0 },
] as const;

export const CONTROLLED_TEST_RECIPIENTS = [
  'marketing+testing@greeleyhatworks.com',
  'marketing@greeleyhatworks.com',
  'reu+testingghw@localnerds.co',
  'reufroman@gmail.com',
] as const;

export function isControlledTestRecipient(email: string | null | undefined): boolean {
  const normalized = (email ?? '').trim().toLowerCase();
  return CONTROLLED_TEST_RECIPIENTS.some((recipient) => recipient === normalized);
}

export interface ValidationError {
  code: string;
  field: string;
  message: string;
}

export interface ShippingAddress {
  address1: string;
  address2: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  countryCode: string;
}

export interface ShippingOrderSnapshot {
  verified: boolean;
  verificationSource?: 'shopify_api' | 'controlled_test_evidence';
  recipientEmail: string | null;
  orderNumber: string;
  orderDate: string;
  customerFirstName: string;
  customerLastName: string;
  shippingAddress: ShippingAddress | null;
  items: Array<{ id: string; title: string; quantity: number }>;
}

export interface ShippingFields {
  order: ShippingOrderSnapshot;
  selectedItemIds: string[];
  addressConfirmed: boolean;
  internationalAddressConfirmed: boolean;
  addressOverride: ShippingAddress | null;
}

export type FollowUpReason = 'product_link' | 'recraft' | 'appointment' | 'other';

export interface FollowUpFields {
  customerFirstName: string;
  customerLastName: string;
  recipientEmail: string;
  orderNumber?: string;
  orderDate?: string;
  reasons: FollowUpReason[];
  productLink?: string;
  appointmentDate?: string;
  otherNotes?: string;
}

export interface CustomOptionsRequestFields {
  customerFirstName: string;
  customerLastName: string;
  recipientEmail: string;
  orderNumber?: string;
}

export interface GeneratedComposition {
  subject: string;
  body: string;
}

export interface ShippingGenerationFacts extends Record<string, unknown> {
  customerFirstName: string;
  customerLastName: string;
  recipientEmail: string | null;
  orderNumber: string;
  orderDate: string;
  shippingAddress: ShippingAddress | null;
  selectedItems: ShippingOrderSnapshot['items'];
}

export interface FollowUpGenerationFacts extends Record<string, unknown> {
  customerFirstName: string;
  customerLastName: string;
  recipientEmail: string;
  orderNumber?: string;
  orderDate?: string;
  reasons: FollowUpReason[];
  productLink?: string;
  recraft?: string;
  appointmentDate?: string;
  otherNotes?: string;
}

function error(code: string, field: string, message: string): ValidationError {
  return { code, field, message };
}

function isValidEmail(value: string | null | undefined): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value ?? '').trim());
}

function isValidHttpUrl(value: string | null | undefined): boolean {
  try {
    const url = new URL(value ?? '');
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

export function isDomesticShippingAddress(address: Partial<ShippingAddress> | null): boolean {
  if (!address) return false;
  const code = typeof address.countryCode === 'string' ? address.countryCode.trim().toUpperCase() : '';
  const country = typeof address.country === 'string' ? address.country.trim().toLowerCase() : '';
  const codeIsDomestic = code === 'US' || code === 'USA';
  const countryIsDomestic = country === 'united states' || country === 'united states of america';
  if (code && country) return codeIsDomestic && countryIsDomestic;
  return code ? codeIsDomestic : countryIsDomestic;
}

export function validateShippingFields(fields: ShippingFields): ValidationError[] {
  const errors: ValidationError[] = [];
  if (!fields.order.verified) {
    errors.push(error('order_not_verified', 'order_number', 'Verify the Shopify order before sending.'));
  }
  if (!isValidEmail(fields.order.recipientEmail)) {
    errors.push(error('recipient_not_verified', 'recipient_email', 'The Shopify order must have a valid customer email.'));
  }
  const controlledTestEvidence = fields.order.verificationSource === 'controlled_test_evidence';
  const effectiveAddress = fields.addressOverride ?? fields.order.shippingAddress;
  if (controlledTestEvidence) {
    if (!fields.addressOverride) {
      errors.push(error(
        'controlled_test_address_override_required',
        'address_override',
        'Enter the shipping address manually for this controlled test.',
      ));
    }
    if (!effectiveAddress) {
      errors.push(error('shipping_address_missing', 'shipping_address', 'Enter and confirm a shipping address before sending.'));
    }
  } else if (!fields.order.shippingAddress) {
    errors.push(error('shipping_address_missing', 'shipping_address', 'The Shopify order has no shipping address.'));
  }
  if (fields.selectedItemIds.length === 0) {
    errors.push(error('items_required', 'selected_item_ids', 'Select at least one verified order item.'));
  } else {
    const known = new Set(fields.order.items.map((item) => item.id));
    if (fields.selectedItemIds.some((id) => !known.has(id))) {
      errors.push(error('item_not_verified', 'selected_item_ids', 'Every selected item must belong to the verified order.'));
    }
  }
  if (!fields.addressConfirmed) {
    errors.push(error('address_confirmation_required', 'address_confirmed', 'Confirm the shipping address before sending.'));
  }
  if (effectiveAddress && !isDomesticShippingAddress(effectiveAddress) && !fields.internationalAddressConfirmed) {
    errors.push(error(
      'international_confirmation_required',
      'international_address_confirmed',
      'Confirm the international address before sending.',
    ));
  }
  if (fields.addressOverride) {
    for (const key of ['address1', 'city', 'province', 'postalCode', 'country'] as const) {
      if (!fields.addressOverride[key].trim()) {
        errors.push(error('address_override_incomplete', `address_override.${key}`, 'Complete every required address field.'));
      }
    }
  }
  return errors;
}

export function validateFollowUpFields(fields: FollowUpFields): ValidationError[] {
  const errors: ValidationError[] = [];
  if (!fields.customerFirstName.trim()) {
    errors.push(error('customer_first_name_required', 'customer_first_name', 'Customer first name is required.'));
  }
  if (!fields.customerLastName.trim()) {
    errors.push(error('customer_last_name_required', 'customer_last_name', 'Customer last name is required.'));
  }
  if (!isValidEmail(fields.recipientEmail)) {
    errors.push(error('recipient_invalid', 'recipient_email', 'Enter a valid recipient email.'));
  }
  if (fields.reasons.length === 0) {
    errors.push(error('reason_required', 'reasons', 'Choose at least one follow-up reason.'));
  }
  if (fields.reasons.includes('product_link') && !isValidHttpUrl(fields.productLink)) {
    errors.push(error('product_link_invalid', 'product_link', 'Enter a valid product link.'));
  }
  if (fields.reasons.includes('appointment') && !fields.appointmentDate?.trim()) {
    errors.push(error('appointment_date_required', 'appointment_date', 'Appointment date is required.'));
  }
  if (fields.reasons.includes('other') && !fields.otherNotes?.trim()) {
    errors.push(error('other_notes_required', 'other_notes', 'Explain the other follow-up reason.'));
  }
  return errors;
}

export function validateCustomOptionsRequestFields(fields: CustomOptionsRequestFields): ValidationError[] {
  const errors: ValidationError[] = [];
  if (!fields.customerFirstName.trim()) {
    errors.push(error('customer_first_name_required', 'customer_first_name', 'Customer first name is required.'));
  }
  if (!fields.customerLastName.trim()) {
    errors.push(error('customer_last_name_required', 'customer_last_name', 'Customer last name is required.'));
  }
  if (!isValidEmail(fields.recipientEmail)) {
    errors.push(error('recipient_invalid', 'recipient_email', 'Enter a valid recipient email.'));
  }
  return errors;
}

export function buildShippingGenerationFacts(fields: ShippingFields): ShippingGenerationFacts {
  const selected = new Set(fields.selectedItemIds);
  return {
    customerFirstName: fields.order.customerFirstName,
    customerLastName: fields.order.customerLastName,
    recipientEmail: fields.order.recipientEmail,
    orderNumber: fields.order.orderNumber,
    orderDate: fields.order.orderDate,
    shippingAddress: fields.addressOverride ?? fields.order.shippingAddress,
    selectedItems: fields.order.items.filter((item) => selected.has(item.id)),
  };
}

export function buildFollowUpGenerationFacts(fields: FollowUpFields): FollowUpGenerationFacts {
  return {
    customerFirstName: fields.customerFirstName,
    customerLastName: fields.customerLastName,
    recipientEmail: fields.recipientEmail,
    orderNumber: fields.orderNumber,
    orderDate: fields.orderDate,
    reasons: fields.reasons,
    productLink: fields.reasons.includes('product_link') ? fields.productLink : undefined,
    recraft: fields.reasons.includes('recraft') ? RECRAFT_URL : undefined,
    appointmentDate: fields.reasons.includes('appointment') ? fields.appointmentDate : undefined,
    otherNotes: fields.reasons.includes('other') ? fields.otherNotes : undefined,
  };
}

export function buildCustomOptionsRequestEmail(
  fields: CustomOptionsRequestFields,
  templateVersion: number,
): GeneratedComposition {
  if (templateVersion === 2) {
    const body = [
      `Hi ${fields.customerFirstName},`,
      '',
      'We’re excited to take the next step in crafting your custom Greeley Hat Works hat! Every hat we build is tailored specifically for you, and we’re ready to bring your vision to life.',
      '',
      'To get started, please explore our custom choices here:',
      CUSTOM_OPTIONS_URL,
      '',
      'Whenever you’re ready, simply reply to this email with your preferences for:',
      '',
      ...CUSTOM_OPTIONS_V2_CHECKLIST.map((item) => `${'  '.repeat(item.depth)}- ${item.text}`),
      '',
      `If you're stuck on any details or torn between a few options, don't worry! Just tell us what you're imagining, and our team will gladly offer guidance to ensure your hat turns out exactly how you want it. If you prefer to speak with a member of our team, they would be happy to assist you in the design process: ${CUSTOM_OPTIONS_PHONE}.`,
      '',
      'We look forward to getting your custom build started!',
      '',
      'Warm regards,',
    ].join('\n');

    return {
      subject: 'Ready to Design Your Custom Greeley Hat!',
      body: appendTeamSignature(body),
    };
  }
  if (templateVersion !== 1) {
    throw new Error(`Unsupported custom-options template version: ${templateVersion}`);
  }

  const orderContext = fields.orderNumber
    ? ` for order ${fields.orderNumber}`
    : '';
  const body = [
    `Hi ${fields.customerFirstName},`,
    '',
    `We're ready to collect the custom options for your Greeley Hat Works hat${orderContext}.`,
    `Please review the Custom Hat Options page: ${CUSTOM_OPTIONS_URL}`,
    '',
    'Reply with your selections for:',
    ...CUSTOM_OPTIONS_V1_CHECKLIST.map((item) => `- ${item}`),
    '',
    'If you are unsure about any option, tell us what you are considering and our team will help you finalize it.',
  ].join('\n');

  return {
    subject: 'Your Greeley Hat Works custom options',
    body: appendTeamSignature(body),
  };
}

export function validateCustomOptionsFinalCopy(body: string, templateVersion: number): ValidationError[] {
  const requiredOptions = templateVersion === 1
    ? CUSTOM_OPTIONS_V1_CHECKLIST
    : templateVersion === 2
      ? CUSTOM_OPTIONS_V2_CHECKLIST.map((item) => item.text)
      : null;
  if (!requiredOptions) {
    return [error(
      'custom_options_version_unsupported',
      'template_version',
      `Custom-options template version ${templateVersion} is not supported.`,
    )];
  }

  const errors: ValidationError[] = [];
  if (!body.includes(CUSTOM_OPTIONS_URL)) {
    errors.push(error(
      'custom_options_url_missing',
      'final_body',
      'The reviewed email must include the Custom Hat Options page.',
    ));
  }
  if (templateVersion === 2 && !body.includes(CUSTOM_OPTIONS_PHONE)) {
    errors.push(error(
      'custom_options_phone_missing',
      'final_body',
      `The reviewed email must include the approved phone number: ${CUSTOM_OPTIONS_PHONE}`,
    ));
  }
  for (const item of requiredOptions) {
    if (!body.includes(item)) {
      errors.push(error(
        'custom_option_missing',
        'final_body',
        `The reviewed email must keep this required option: ${item}`,
      ));
    }
  }
  return errors;
}

export function parseGeneratedComposition(raw: string): GeneratedComposition {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    throw new Error('Generation must be one strict JSON object without markdown.');
  }
  const parsed: unknown = JSON.parse(trimmed);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Generation must be a JSON object.');
  }
  const object = parsed as Record<string, unknown>;
  const keys = Object.keys(object).sort();
  if (keys.join(',') !== 'body,subject') {
    throw new Error('Generation must contain exactly subject and body.');
  }
  if (typeof object.subject !== 'string' || typeof object.body !== 'string') {
    throw new Error('Generated subject and body must be strings.');
  }
  const subject = object.subject.trim();
  const body = object.body.trim();
  if (!subject || !body) throw new Error('Generated subject and body cannot be empty.');
  if (/{{[^}]+}}|\[[A-Z][^\]]*\]/.test(`${subject}\n${body}`)) {
    throw new Error('Generated email contains an unresolved placeholder.');
  }
  return { subject, body };
}

export function appendTeamSignature(body: string): string {
  const withoutDuplicate = body
    .trim()
    .replace(new RegExp(`(?:\\n\\s*)*${TEAM_SIGNATURE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`), '')
    .trim();
  return `${withoutDuplicate}\n\n${TEAM_SIGNATURE}`;
}

export function assertGenerationIsCurrent(fieldRevision: number, generationRevision: number | null): void {
  if (generationRevision === null || generationRevision !== fieldRevision) {
    throw new Error('Fields changed after generation. Regenerate the draft before sending.');
  }
}
